import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  memo,
  useEffect,
  useRef,
  type ReactNode,
} from "react";
import { Box, Table, Flex, IconButton, Checkbox } from "@radix-ui/themes";
import {
  Pencil1Icon,
  TrashIcon,
  CaretUpIcon,
  CaretDownIcon,
  CaretSortIcon,
} from "@radix-ui/react-icons";
import { useManagerContext } from "./context";

type ColumnDef<T> = {
  columnId: string;
  header: string;
  accessor: (item: T) => ReactNode;
  width?: string;
  sortKey?: string;
};

type CustomActionDef<T> = {
  id: string;
  render: (item: T) => ReactNode;
};

type TableContextValue<T> = {
  columns: ColumnDef<T>[];
  registerColumn: (column: ColumnDef<T>) => void;
  customActions: CustomActionDef<T>[];
  registerCustomAction: (action: CustomActionDef<T>) => void;
  showEdit: boolean;
  showDelete: boolean;
};

// Generic contexts use `never` with type assertion at Provider - type safety enforced via useContext generic
const TableContext = createContext<TableContextValue<never> | null>(null);

type DataTableProps = {
  children: ReactNode;
};

export function DataTable<T>({ children }: DataTableProps) {
  const {
    data,
    openEdit,
    openDelete,
    sortParams,
    sortableColumns,
    setSort,
    allowSelection,
    selectedIds,
    allSelected,
    someSelected,
    toggleSelectAll,
    toggleSelectItem,
  } = useManagerContext<T>();
  const [columns, setColumns] = useState<ColumnDef<T>[]>([]);
  const [customActionIds, setCustomActionIds] = useState<string[]>([]);
  const customActionsRef = useRef<Map<string, (item: T) => ReactNode>>(
    new Map(),
  );
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const columnIndexRef = useRef(0);

  // Reset column index when columns are cleared (component remount)
  useEffect(() => {
    columnIndexRef.current = 0;
  }, []);

  const registerColumn = useCallback((column: ColumnDef<T>) => {
    setColumns((prev) => {
      // Check if column with same id already exists
      const existingIndex = prev.findIndex(
        (c) => c.columnId === column.columnId,
      );
      if (existingIndex >= 0) {
        // Only update if header actually changed
        if (prev[existingIndex].header === column.header) {
          return prev; // No change needed
        }
        // Update existing column (header may have changed due to language)
        const updated = [...prev];
        updated[existingIndex] = column;
        return updated;
      }
      return [...prev, column];
    });
  }, []);

  const registerCustomAction = useCallback((action: CustomActionDef<T>) => {
    // Always update the render function in the ref (no state change)
    customActionsRef.current.set(action.id, action.render);
    // Only trigger state update if this is a new action id
    setCustomActionIds((prev) => {
      if (prev.includes(action.id)) return prev;
      return [...prev, action.id];
    });
  }, []);

  // Build customActions array from ref for consumption
  const customActions = useMemo(() => {
    return customActionIds.map((id) => ({
      id,
      render: (item: T) => {
        const renderFn = customActionsRef.current.get(id);
        return renderFn ? renderFn(item) : null;
      },
    }));
  }, [customActionIds]);

  // Check for Actions children
  useEffect(() => {
    React.Children.forEach(children, (child) => {
      if (React.isValidElement(child) && child.type === Actions) {
        const actionsChild = child as React.ReactElement<{
          children: ReactNode;
        }>;
        React.Children.forEach(actionsChild.props.children, (actionChild) => {
          if (React.isValidElement(actionChild)) {
            if (actionChild.type === EditAction) setShowEdit(true);
            if (actionChild.type === DeleteAction) setShowDelete(true);
          }
        });
      }
    });
  }, [children]);

  const tableContextValue = useMemo(
    () => ({
      columns,
      registerColumn,
      customActions,
      registerCustomAction,
      showEdit,
      showDelete,
    }),
    [
      columns,
      registerColumn,
      customActions,
      registerCustomAction,
      showEdit,
      showDelete,
    ],
  );

  const handleEdit = useCallback(
    (item: T) => {
      openEdit(item);
    },
    [openEdit],
  );

  const handleDelete = useCallback(
    (item: T) => {
      openDelete(item);
    },
    [openDelete],
  );

  // Memoized row component
  const MemoizedRow = useMemo(() => {
    return memo(function TableRow({
      item,
      columns,
      customActions,
      showEdit,
      showDelete,
      showSelection,
      isSelected,
      onEdit,
      onDelete,
      onToggleSelect,
    }: {
      item: T;
      columns: ColumnDef<T>[];
      customActions: CustomActionDef<T>[];
      showEdit: boolean;
      showDelete: boolean;
      showSelection: boolean;
      isSelected: boolean;
      onEdit: (item: T) => void;
      onDelete: (item: T) => void;
      onToggleSelect: (item: T) => void;
    }) {
      const hasActions = showEdit || showDelete || customActions.length > 0;
      return (
        <Table.Row>
          {showSelection && (
            <Table.Cell>
              <Checkbox
                checked={isSelected}
                onCheckedChange={() => onToggleSelect(item)}
              />
            </Table.Cell>
          )}
          {columns.map((col) => (
            <Table.Cell key={col.columnId}>{col.accessor(item)}</Table.Cell>
          ))}
          {hasActions && (
            <Table.Cell>
              <Flex gap="1">
                {customActions.map((action, index) => (
                  <React.Fragment key={index}>
                    {action.render(item)}
                  </React.Fragment>
                ))}
                {showEdit && (
                  <IconButton
                    variant="ghost"
                    size="1"
                    onClick={() => onEdit(item)}
                    title="Edit"
                  >
                    <Pencil1Icon />
                  </IconButton>
                )}
                {showDelete && (
                  <IconButton
                    variant="ghost"
                    size="1"
                    color="red"
                    onClick={() => onDelete(item)}
                    title="Delete"
                  >
                    <TrashIcon />
                  </IconButton>
                )}
              </Flex>
            </Table.Cell>
          )}
        </Table.Row>
      );
    });
  }, []);

  const getItemId = useCallback((item: T): string | number => {
    return (item as { id?: string | number }).id ?? 0;
  }, []);

  const handleToggleSelect = useCallback(
    (item: T) => {
      toggleSelectItem(getItemId(item));
    },
    [toggleSelectItem, getItemId],
  );

  const tableBody = useMemo(
    () =>
      data.map((item, index) => (
        <MemoizedRow
          key={(item as { id?: string | number }).id ?? index}
          item={item}
          columns={columns}
          customActions={customActions}
          showEdit={showEdit}
          showDelete={showDelete}
          showSelection={allowSelection}
          isSelected={selectedIds.has(getItemId(item))}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onToggleSelect={handleToggleSelect}
        />
      )),
    [
      data,
      columns,
      customActions,
      showEdit,
      showDelete,
      allowSelection,
      selectedIds,
      handleEdit,
      handleDelete,
      handleToggleSelect,
      MemoizedRow,
      getItemId,
    ],
  );

  return (
    <TableContext.Provider
      value={tableContextValue as unknown as TableContextValue<never>}
    >
      {/* Render children to register columns */}
      <Box style={{ display: "none" }}>{children}</Box>

      <Table.Root size="1">
        <Table.Header>
          <Table.Row>
            {allowSelection && (
              <Table.ColumnHeaderCell width="40px">
                <Checkbox
                  checked={
                    allSelected ? true : someSelected ? "indeterminate" : false
                  }
                  onCheckedChange={() => toggleSelectAll()}
                />
              </Table.ColumnHeaderCell>
            )}
            {columns.map((col) => {
              const isSortable =
                col.sortKey && sortableColumns.has(col.sortKey);
              const isSorted = sortParams?.column === col.sortKey;
              const sortOrder = isSorted ? sortParams?.order : null;

              return (
                <Table.ColumnHeaderCell
                  key={col.columnId}
                  width={col.width}
                  style={isSortable ? { cursor: "pointer" } : undefined}
                  onClick={
                    isSortable && col.sortKey
                      ? () => setSort(col.sortKey!)
                      : undefined
                  }
                >
                  <Flex align="center" gap="1">
                    {col.header}
                    {isSortable && (
                      <Box
                        style={{
                          color: isSorted
                            ? "var(--accent-11)"
                            : "var(--gray-a8)",
                        }}
                      >
                        {sortOrder === "asc" ? (
                          <CaretUpIcon width="14" height="14" />
                        ) : sortOrder === "desc" ? (
                          <CaretDownIcon width="14" height="14" />
                        ) : (
                          <CaretSortIcon width="14" height="14" />
                        )}
                      </Box>
                    )}
                  </Flex>
                </Table.ColumnHeaderCell>
              );
            })}
            {(showEdit || showDelete || customActions.length > 0) && (
              <Table.ColumnHeaderCell width="80px" />
            )}
          </Table.Row>
        </Table.Header>
        <Table.Body>{tableBody}</Table.Body>
      </Table.Root>
    </TableContext.Provider>
  );
}

type ColumnProps<T> = {
  columnId?: string;
  header: string;
  accessor: (item: T) => ReactNode;
  width?: string;
  sortKey?: string;
};

export function Column<T>({
  columnId,
  header,
  accessor,
  width,
  sortKey,
}: ColumnProps<T>) {
  const tableContext = useContext(TableContext) as TableContextValue<T> | null;
  // Use a ref to store a stable ID that doesn't change across re-renders
  const stableIdRef = useRef<string | null>(null);
  if (stableIdRef.current === null) {
    // Generate a stable ID on first render only
    stableIdRef.current =
      columnId ?? `col-${Math.random().toString(36).slice(2, 9)}`;
  }
  const stableId = stableIdRef.current;

  useEffect(() => {
    if (tableContext) {
      tableContext.registerColumn({
        columnId: stableId,
        header,
        accessor,
        width,
        sortKey,
      });
    }
  }, [tableContext, stableId, header, accessor, width, sortKey]);

  return null;
}

type ActionsProps = {
  children: ReactNode;
};

export function Actions({ children }: ActionsProps) {
  // This component is used for detection only
  return <>{children}</>;
}

export function EditAction() {
  return null;
}

export function DeleteAction() {
  return null;
}

type CustomActionProps<T> = {
  id: string;
  render: (item: T) => ReactNode;
};

export function CustomAction<T>({ id, render }: CustomActionProps<T>) {
  const tableContext = useContext(TableContext) as TableContextValue<T> | null;

  useEffect(() => {
    if (tableContext) {
      tableContext.registerCustomAction({ id, render });
    }
  }, [tableContext, id, render]);

  return null;
}
